import isEmpty from 'lodash/isEmpty';

type IFSEGradeInformation = {
    categories: string[];
    filieres: string[];
    cadresEmploi: string[];
    jobsLabel: string[];
};

/// Remove duplicate from an array.
const removeDups = (data: string[]) =>
    data.filter((val, idx) => data.indexOf(val) === idx);

type FilterGradeLinesArgs = {
    gradeLines: string[][];
    category?: string;
    filiere?: string;
    cadreEmploi?: string;
    jobs?: string[];
};

/**
 * Take a list of grade lines, where each line is a tuple (category, filiere, cadre, job),
 * which materialize the link between each of those parameter,
 * and return the list of categories, filieres, cadres and jobs which are compatible with the
 * currently selected category, filiere, etc.
 *
 * Categories and jobs have special rules since they are the first and last parameter,
 * and jobs is a multi-select, but constrained by the previous parameter
 * (i.e, we cannot have two job that have a different cadre).
 *
 * For categories:
 * - We assume that there is always a category selected (i.e it cannot be undefined)
 * - When no other parameter is set, we want to return all category,
 *   since we don't consider it a constraint in this case.
 *   I.e we want the user to be able to select any category.
 * - When another parameter is set, we want to show all the category that is
 *   compatible with this category.
 *   For example, if we have the same filiere "fil1" in two separate category "A" and "B"
 *   then we want to return "A" and "B"
 *
 * For jobs:
 * - When no jobs is selected, we just process as usual and filter based on the other parameter
 * - When a least one job is selected, we need to make sure we return only jobs that are compatible
 *   with the currently selected jobs
 * - To do this we find the parent of the currently selected job (the parameter cadre),
 *   and we only allow jobs that have the same parent.
 *
 * Here is a few example, suppose we have the following grade lines:
 * ```
 * const gradeLines = [
 *  ["A", "fil1", "cadre1", "job1"],
 *  ["A", "fil1", "cadre1", "job2"],
 *  ["B", "fil1", "cadre2", "job3"],
 *  ["B", "fil2", "cadre3", "job4"],
 *  ["B", "fil3", "cadre3", "job5"],
 *  ["C", "fil3", "cadre1", "job6"],
 *  ["C", "fil4", "cadre1", "job7"],
 *  ["D", "fil5", "cadre1", "job8"],
 * ]
 * ```
 *
 * Then, we should get:
 * ```
 * $ filterGradeLines({ gradeLines, category: "A" })
 * => { categories: ["A", "B", "C", "D"], filieres: ["fil1"], cadresEmploi: ["cadre1"], jobs: ["job1", "job2"] }
 * // If we only have the category, we return all category, and the parameter compatible with the selected category.
 *
 * $ filterGradeLines({ gradeLines, category: "B", filiere: "fil3" })
 * => { categories: ["B", "C"], filieres: ["fil3"], cadresEmploi: ["cadre3"], jobs: ["job5"] }
 * // Both category B and C have fil3, so they are both returned, but we only return cadre3 and job5
 * // because they are the only one who have both constaint category B and filiere fil3.
 *
 * $ filterGradeLines({ gradeLines, category: "A", jobs: ["job1"] })
 * => { categories: ["A"], filieres: ["fil1"], cadresEmploi: ["cadre1"], jobs: ["job1", "job2"] }
 * // When we have a job selected, we only return jobs that are also compatible with it.
 * // In this case, only job2 is compatible because it share the same parent "cadre1".
 * ```
 *
 * This code assume that the parameter are coherent.
 * So if you use this function with incompatible filiere and cadre, for example, the return
 * will be undefined.
 *
 * @param options.gradeLines List of grades lines, which are tuple (category, filiere, cadre, job)
 * @param options.category Selected category
 * @param options.filiere Selected filiere
 * @param options.cadreEmploi Selected cadre
 * @param options.jobs Selected jobs
 */
export const filterGradeLines = ({
    gradeLines,
    category,
    filiere,
    cadreEmploi,
    jobs,
}: FilterGradeLinesArgs): IFSEGradeInformation => {
    const categories: string[] = [];
    const filieres: string[] = [];
    const cadresEmploi: string[] = [];
    const jobsLabel: string[] = [];

    // If we have at least one job selected we want to find their parent.
    // This is used so that we only output compatible job to avoid letting
    // the user select jobs that might have different cadre / filiere.
    const possibleCadreEmploi = !isEmpty(jobs)
        ? removeDups(
              gradeLines
                  .filter(([_cat, _fil, _emploi, job]) => jobs?.includes(job))
                  .map(([_cat, _fil, emploi, _job]) => emploi),
          )
        : [];

    gradeLines.forEach(([cat, fil, emploi, job]) => {
        // If we have a filiere selected, we filter out every line that do not
        // have the same filiere.
        if (filiere && filiere !== fil) {
            return;
        }

        // Same for the cadre.
        if (cadreEmploi && cadreEmploi !== emploi) {
            return;
        }

        // Jobs are special since we can select multiple, and we want to
        // return all the possible compatible option, even when one is selected.
        if (!isEmpty(jobs) && !jobs?.includes(job)) {
            const filiereAndCadreValid =
                filiere === fil && possibleCadreEmploi.includes(emploi);
            const validCadre = cadreEmploi === emploi;
            const emptyFormButPossibleCadre =
                cat === category &&
                (isEmpty(filiere) || isEmpty(cadreEmploi)) &&
                possibleCadreEmploi.includes(emploi);

            // We want to add the job to the list of selectable jobs when:
            // 1. The job has a filiere and a cadre compatible with what we already selected.
            // 2. The parent (cadre) of the selected job is the same as this line.
            // 3. We have neither cadre and filiere selected, but the cadre of this line is compatible
            //    with the currently selected job.
            if (
                filiereAndCadreValid ||
                validCadre ||
                emptyFormButPossibleCadre
            ) {
                categories.push(cat);
                filieres.push(fil);
                jobsLabel.push(job);
                cadresEmploi.push(emploi);
            }
            return;
        }

        // If everything is compatible, or we have nothing selected, including the category, we add the params of this line to the lists.
        if (category === cat) {
            categories.push(cat);
            filieres.push(fil);
            cadresEmploi.push(emploi);
            jobsLabel.push(job);
        }
        // when the category is not the same as the one selected, we check if the line category is compatible
        // with our current constaints and return it if it is.
        else if (
            filiere === fil ||
            cadreEmploi === emploi ||
            (jobs !== undefined && jobs.includes(job))
        ) {
            categories.push(cat);
        } else if (isEmpty(filiere) && isEmpty(cadreEmploi) && isEmpty(jobs)) {
            categories.push(cat);
        }
    });

    return {
        categories: removeDups(categories),
        filieres: removeDups(filieres),
        cadresEmploi: removeDups(cadresEmploi),
        jobsLabel: removeDups(jobsLabel),
    };
};
